import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input } from '@components';
import { InviteUserData } from '../types';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteUserData) => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ isOpen, onClose, onInvite }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<InviteUserData>({
    email: '',
    firstName: '',
    lastName: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onInvite(formData);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Einladung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('users.inviteNewUser')}
          </h2>
          <Button variant="ghost" size="sm" onClick={handleClose} className="p-1 h-8 w-8">
            <span className="material-icons text-lg">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.emailAddress')}
            </label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('users.enterEmailAddress')}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.emailAddressDescription')}
            </p>
          </div>

          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.firstNameRequired')}
            </label>
            <Input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={e => setFormData({ ...formData, firstName: e.target.value })}
              placeholder={t('users.enterFirstName')}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.firstNameDescription')}
            </p>
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.lastNameRequired')}
            </label>
            <Input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={e => setFormData({ ...formData, lastName: e.target.value })}
              placeholder={t('users.enterLastName')}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.lastNameDescription')}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={loading}>
              {t('users.cancel')}
            </Button>
            <Button type="submit" variant="gradient-multi" disabled={loading}>
              {loading ? t('users.sending') : t('users.sendInvite')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
